import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { Routes, Route, Outlet, useLocation, useParams } from "@/lib/router";
import { PREFIX_LANGS } from "@/lib/i18nPath";
import { LangSuggest } from "@/components/LangSuggest";
import { CatalogResolver } from "@/components/catalog/CatalogPage";
import { HelmetProvider } from "react-helmet-async";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CookieConsent } from "@/components/CookieConsent";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { ConciergeChat } from "@/components/ConciergeChat";
import { RouteSeo } from "@/components/RouteSeo";
import { StructuredData } from "@/components/StructuredData";
import { Analytics } from "@/components/Analytics";
import { MiniCart } from "@/components/store/MiniCart";
import { ClubReturnFab } from "@/components/club/ClubReturnFab";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Toaster } from "@/components/ui/sonner";
import { Lightbox } from "@/components/Lightbox";
import { captureAffiliate } from "@/lib/affiliate";
import { initAnalyticsIfConsented, trackPageview, startPresence, updatePresencePath } from "@/lib/analytics";

const Home = lazy(() => import("@/pages/Home"));
const Story = lazy(() => import("@/pages/Story"));
const Products = lazy(() => import("@/pages/Products"));
const Explore = lazy(() => import("@/pages/Explore"));
const Contact = lazy(() => import("@/pages/Contact"));
const FounderClub = lazy(() => import("@/pages/FounderClub"));
const Signature = lazy(() => import("@/pages/Signature"));
const Seasonal = lazy(() => import("@/pages/Seasonal"));
const Discovery = lazy(() => import("@/pages/Discovery"));
const CollectionXL = lazy(() => import("@/pages/CollectionXL"));
const Journal = lazy(() => import("@/pages/Journal"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Privacy = lazy(() => import("@/pages/LegalPages").then((m) => ({ default: m.Privacy })));
const Terms = lazy(() => import("@/pages/LegalPages").then((m) => ({ default: m.Terms })));
const Cookies = lazy(() => import("@/pages/LegalPages").then((m) => ({ default: m.Cookies })));
const Sales = lazy(() => import("@/pages/LegalPages").then((m) => ({ default: m.Sales })));
const Returns = lazy(() => import("@/pages/LegalPages").then((m) => ({ default: m.Returns })));
const Shipping = lazy(() => import("@/pages/LegalPages").then((m) => ({ default: m.Shipping })));
// Store
const Shop = lazy(() => import("@/pages/shop/Shop"));
const ProductDetail = lazy(() => import("@/pages/shop/ProductDetail"));
const ReviewInvite = lazy(() => import("@/pages/shop/ReviewInvite"));
const Cart = lazy(() => import("@/pages/shop/Cart"));
const Checkout = lazy(() => import("@/pages/shop/Checkout"));
const OrderConfirmation = lazy(() => import("@/pages/shop/OrderConfirmation"));
const Account = lazy(() => import("@/pages/account/Account"));
const ResetPassword = lazy(() => import("@/pages/account/ResetPassword"));
const Admin = lazy(() => import("@/pages/admin/Admin"));
// VORNX Club (standalone PWA experience)
const ClubLayout = lazy(() => import("@/pages/club/ClubLayout"));
const ClubHome = lazy(() => import("@/pages/club/ClubHome"));
const ClubMagazine = lazy(() => import("@/pages/club/ClubMagazine"));
const ClubProfile = lazy(() => import("@/pages/club/ClubProfile"));

const RouteEffects = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
    trackPageview(pathname);
    updatePresencePath(pathname);
  }, [pathname]);
  return null;
};

// Public routes live under an optional language prefix; unknown prefixes are 404.
const LangGuard = () => {
  const { lang } = useParams();
  // Not a language prefix -> IT root path (e.g. /candele-profumate, /fragranze/black, /journal/x): resolve via catalog.
  if (lang && !PREFIX_LANGS.includes(lang)) return <CatalogResolver />;
  return <Outlet />;
};

// Checkout & Admin are distraction-free: no global chrome.
const Chrome = ({ children }) => {
  const { pathname } = useLocation();
  const bare = pathname.startsWith("/checkout") || pathname.startsWith("/admin") || pathname.startsWith("/club");
  if (bare) return children;
  return (
    <>
      <Navbar />
      {children}
      <Footer />
      <WhatsAppButton />
      <ConciergeChat />
      <LangSuggest />
    </>
  );
};

function App() {
  useEffect(() => {
    initAnalyticsIfConsented();
    startPresence();
    captureAffiliate();
  }, []);

  return (
    <HelmetProvider>
      <div className="App">
          <RouteEffects />
          <RouteSeo />
          <StructuredData />
          <Analytics />
          <Chrome>
            <ErrorBoundary>
              <Suspense fallback={<div className="min-h-screen bg-ink" />}>
                <Routes>
                <Route path="/:lang?" element={<LangGuard />}>
                  <Route index element={<Home />} />
                  <Route path="story" element={<Story />} />
                  <Route path="explore" element={<Explore />} />
                  <Route path="products" element={<Products />} />
                  <Route path="signature" element={<Signature />} />
                  <Route path="seasonal" element={<Seasonal />} />
                  <Route path="discovery" element={<Discovery />} />
                  <Route path="collections/:variant" element={<CollectionXL />} />
                  <Route path="journal" element={<Journal />} />
                  <Route path="contact" element={<Contact />} />
                  <Route path="founder-club" element={<FounderClub />} />
                  <Route path="privacy" element={<Privacy />} />
                  <Route path="terms" element={<Terms />} />
                  <Route path="cookies" element={<Cookies />} />
                  <Route path="sales" element={<Sales />} />
                  <Route path="returns" element={<Returns />} />
                  <Route path="shipping" element={<Shipping />} />
                  <Route path="shop" element={<Shop />} />
                  <Route path="shop/:slug" element={<ProductDetail />} />
                  <Route path="*" element={<CatalogResolver />} />
                </Route>
                {/* Store */}
                <Route path="/review/:token" element={<ReviewInvite />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/order/:id" element={<OrderConfirmation />} />
                <Route path="/account" element={<Account />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/admin" element={<Admin />} />
                {/* VORNX Club — standalone app experience */}
                <Route path="/club" element={<ClubLayout />}>
                  <Route index element={<ClubHome />} />
                  <Route path="magazine" element={<ClubMagazine />} />
                  <Route path="magazine/:slug" element={<ClubMagazine />} />
                  <Route path="profilo" element={<ClubProfile />} />
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
            </ErrorBoundary>
          </Chrome>
          <MiniCart />
          <ClubReturnFab />
          <CookieConsent />
          <Toaster />
          <Lightbox />
      </div>
    </HelmetProvider>
  );
}

export default App;
